import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { BuyerAuthContext } from '../../contexts/buyer-auth-context-provider';
import useResendCooldown from './use-resend-cooldown';

const OTP_FIELD_ID = 'buyer-otp-code';

const schema = z.object({
  code: z.string().regex(/^\d{4}$/, 'Enter the 4-digit code'),
});

// Keyed on the server's machine-readable `code`, not on its message, so copy
// can change on either side without breaking the other.
const ERROR_COPY = {
  ERR_BUYER_OTP_INVALID: 'That code is not right.',
  ERR_BUYER_OTP_EXPIRED: 'That code has expired. Send a new one.',
  ERR_BUYER_OTP_ATTEMPTS_EXCEEDED: 'Too many incorrect attempts. Send a new code to try again.',
  ERR_BUYER_OTP_THROTTLED: 'Too many requests. Please wait a moment before trying again.',
  ERR_BUYER_OTP_SEND_FAILED: 'We could not send the code just now. Please try again.',
  ERR_BUYER_ORDER_NOT_VERIFIABLE:
    'This payment link cannot be verified online. Please contact support to continue.',
};

// States where a new code is the only way forward, so the resend is promoted
// and the input is put away rather than left inviting another wrong guess.
const NEEDS_NEW_CODE = new Set([
  'ERR_BUYER_OTP_EXPIRED',
  'ERR_BUYER_OTP_ATTEMPTS_EXCEEDED',
]);

/**
 * The gate a student sees when they open a payment link we cannot yet attribute
 * to them.
 *
 * There is no field for a phone number, by design. The number is whatever the
 * order already carries; the page only ever learns its masked form. A screen
 * that could nominate a number could mint a session for one it controls.
 */
function OtpLogin() {
  const {
    maskedPhone, verifiable, busy, error, attemptsLeft, resendAt, sendOtp, verifyOtp,
  } = useContext(BuyerAuthContext);

  const [sent, setSent] = useState(false);
  const secondsLeft = useResendCooldown(resendAt);

  const {
    register, handleSubmit, formState: { errors }, reset, setFocus,
  } = useForm({ resolver: zodResolver(schema), defaultValues: { code: '' } });

  // Destructured rather than spread, matching components/terms.
  const codeField = register('code');

  const serverCode = error?.code;
  const needsNewCode = NEEDS_NEW_CODE.has(serverCode);
  const message = serverCode
    ? ERROR_COPY[serverCode] || 'We could not check that code. Please try again.'
    : null;

  // A wrong code clears the field and puts the cursor back in it, so the next
  // attempt does not start by deleting four digits.
  useEffect(() => {
    if (serverCode === 'ERR_BUYER_OTP_INVALID') {
      reset({ code: '' });
      setFocus('code');
    }
  }, [serverCode, error, reset, setFocus]);

  const onSend = async () => {
    await sendOtp();
    setSent(true);
  };

  const onSubmit = async ({ code }) => {
    await verifyOtp(code);
  };

  if (!verifiable) {
    return (
      <div className="flex justify-center px-4 py-10">
        <div className="w-full max-w-[460px] rounded-[8px] border border-[#E2E8F0] bg-white p-6 text-center">
          <h1 className="text-[18px] font-semibold text-[#334155]">We cannot verify this link</h1>
          <p className="mt-3 text-[14px] text-[#64748B]">
            {ERROR_COPY.ERR_BUYER_ORDER_NOT_VERIFIABLE}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center px-4 py-10">
      <div className="w-full max-w-[460px] rounded-[8px] border border-[#E2E8F0] bg-white p-6">
        <h1 className="text-[18px] md:text-[20px] font-semibold text-[#334155]">
          Verify your mobile number
        </h1>

        <p className="mt-2 text-[14px] text-[#64748B]">
          {sent ? 'We sent a 4-digit code to ' : 'To open this payment link, we will send a 4-digit code to '}
          {maskedPhone ? (
            // Read aloud as six asterisks otherwise.
            <span className="font-semibold text-[#334155]" aria-label={`the number ending ${maskedPhone.slice(-4)}`}>
              {maskedPhone}
            </span>
          ) : 'the number registered with this order'}
          .
        </p>

        <div aria-live="polite" className="sr-only">
          {sent ? `Code sent to ${maskedPhone || 'your registered number'}` : ''}
        </div>

        {!sent ? (
          <button
            type="button"
            onClick={onSend}
            disabled={busy}
            className={`mt-6 w-full py-4 font-bold text-[16px] text-white rounded-[6px] ${
              busy ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#045692]'
            }`}
          >
            {busy ? 'Sending...' : 'Send code'}
          </button>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
            {!needsNewCode && (
              <label htmlFor={OTP_FIELD_ID} className="block text-[14px] font-medium text-[#334155]">
                Enter the 4-digit code
                <input
                  id={OTP_FIELD_ID}
                  // Not type="number": it ignores maxLength, adds spinners,
                  // and mangles a leading zero.
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={4}
                  name={codeField.name}
                  ref={codeField.ref}
                  onChange={codeField.onChange}
                  onBlur={codeField.onBlur}
                  aria-invalid={Boolean(message || errors.code)}
                  aria-describedby={`${OTP_FIELD_ID}-error`}
                  className="mt-2 w-full rounded-[6px] border border-[#CBD5E1] px-4 py-3 text-[20px] tracking-[0.5em] text-center text-[#334155] focus:border-[#045692] focus:outline-none"
                />
              </label>
            )}

            <p id={`${OTP_FIELD_ID}-error`} role="alert" className="mt-2 min-h-[20px] text-[13px] text-[#DC2626]">
              {message || errors.code?.message || ''}
              {message && serverCode === 'ERR_BUYER_OTP_INVALID' && attemptsLeft != null
                ? ` ${attemptsLeft} ${attemptsLeft === 1 ? 'attempt' : 'attempts'} left.`
                : ''}
            </p>

            {!needsNewCode && (
              <button
                type="submit"
                disabled={busy}
                className={`mt-2 w-full py-4 font-bold text-[16px] text-white rounded-[6px] ${
                  busy ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#045692]'
                }`}
              >
                {busy ? 'Verifying...' : 'Verify'}
              </button>
            )}

            <button
              type="button"
              onClick={onSend}
              disabled={busy || secondsLeft > 0}
              className={`mt-3 w-full py-3 text-[15px] font-semibold rounded-[6px] border ${
                busy || secondsLeft > 0
                  ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                  : 'border-[#045692] text-[#045692]'
              }`}
            >
              {secondsLeft > 0 ? `Resend in ${secondsLeft}s` : 'Resend code'}
            </button>
          </form>
        )}

        <p className="mt-5 text-[12px] text-[#94A3B8] text-center">
          Not your number? Please contact support to update it before paying.
        </p>
      </div>
    </div>
  );
}

export default OtpLogin;
